import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  CheckCircleIcon,
  StarIcon,
  ArrowRightIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import {
  getAllIntegrationConfigs,
  INTEGRATION_CATEGORIES,
  getIntegrationConfig,
  IntegrationConfig,
} from 'src/config/integrations';
import {
  useListIntegrations,
  useUpdateIntegration,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import { IntegrationKey } from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import { T, Var, useGT } from 'gt-react';
import { HeaderPageTitle } from 'src/context/header-title.context';

export function IntegrationsListPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const { data: integrationsData, isLoading, refetch } = useListIntegrations();
  const installedIntegrations = integrationsData?.installed ?? {};
  const availability = integrationsData?.availability ?? {};
  const updateIntegration = useUpdateIntegration();
  const { showToast } = useToast();
  const [connectingIntegrations, setConnectingIntegrations] = useState<
    Set<IntegrationKey>
  >(new Set());
  const allIntegrations = getAllIntegrationConfigs();

  const handleIntegrationClick = (integrationKey: IntegrationKey) => {
    navigate({
      to: '/integrations/$integrationKey',
      params: { integrationKey },
    });
  };

  const isInstalled = (key: IntegrationKey) => {
    return Boolean(installedIntegrations && installedIntegrations[key]);
  };

  const isAvailable = (key: IntegrationKey) => {
    return availability[key]?.available ?? true;
  };

  const availabilityReason = (key: IntegrationKey) => {
    return availability[key]?.reason;
  };

  const isConnecting = (key: IntegrationKey) => {
    return connectingIntegrations.has(key);
  };

  // Env-gated integrations (hideWhenUnavailable) disappear entirely when
  // the backend reports them unavailable, instead of showing an
  // "Unavailable" card.
  const visibleIntegrations = allIntegrations.filter(
    (integration) =>
      !(integration.hideWhenUnavailable && !isAvailable(integration.key)),
  );

  const handleConnect = async <T extends IntegrationKey>(
    integrationKey: T,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent card click

    const integrationConfig = getIntegrationConfig(integrationKey);

    if (!integrationConfig.defaultSettings) {
      showToast({
        title: gt('Configuration Required', {
          context: 'integration toast title',
        }),
        message: gt('This integration requires manual configuration.', {
          context: 'integration toast message',
        }),
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    if (!isAvailable(integrationKey)) {
      showToast({
        title: gt('Integration Unavailable', {
          context: 'integration toast title',
        }),
        message:
          availabilityReason(integrationKey) ||
          gt('{name} cannot be configured right now.', {
            context: 'integration unavailable message',
            name: integrationConfig.name,
          }),
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setConnectingIntegrations((prev) => new Set(prev).add(integrationKey));

    try {
      await updateIntegration.mutateAsync({
        key: integrationKey,
        settings: integrationConfig.defaultSettings,
      } as Parameters<typeof updateIntegration.mutateAsync>[0]);

      // Refetch the integrations list to update the UI
      await refetch();

      showToast({
        title: gt('Integration Connected', {
          context: 'integration toast title',
        }),
        message: gt(
          '{name} has been successfully connected with default settings.',
          {
            context: 'integration connected toast',
            name: integrationConfig.name,
          },
        ),
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: gt('Connection Failed', {
          context: 'integration toast title',
        }),
        message: gt('Failed to connect {name}. Please try again.', {
          context: 'integration connection failed toast',
          name: integrationConfig.name,
        }),
        color: 'error',
        icon: 'error',
      });
    } finally {
      setConnectingIntegrations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(integrationKey);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div>
          <div className="mb-2">
            <HeaderPageTitle>
              <T context="integrations page title">Integrations</T>
            </HeaderPageTitle>
            <p className="text-sm text-muted-foreground">
              <T context="integrations page subtitle loading">
                Connect your favorite tools and services
              </T>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-2">
        <HeaderPageTitle>
          <T context="integrations page title">Integrations</T>
        </HeaderPageTitle>
        <p className="text-sm text-muted-foreground">
          <T context="integrations page subtitle">
            Connect your favorite tools and services to streamline your workflow
          </T>
        </p>
      </div>

      {/* Featured/Popular Integrations */}
      <FeaturedIntegrations
        allIntegrations={visibleIntegrations}
        isInstalled={isInstalled}
        isAvailable={isAvailable}
        availabilityReason={availabilityReason}
        handleIntegrationClick={handleIntegrationClick}
        handleConnect={handleConnect}
        isConnecting={isConnecting}
        notAvailableFallback={gt('Not available right now', {
          context: 'integration unavailable fallback',
        })}
      />

      {/* All Integrations by Category */}
      <div>
        <h2 className="text-xl font-semibold mb-2">
          <T context="integrations section heading">All Integrations</T>
        </h2>

        {Object.entries(INTEGRATION_CATEGORIES).map(
          ([categoryKey, categoryInfo]) => {
            const categoryIntegrations = visibleIntegrations.filter(
              (integration) => integration.category === categoryKey,
            );

            if (categoryIntegrations.length === 0) return null;

            const CategoryIcon = categoryInfo.icon;

            return (
              <div key={categoryKey} className="mb-3">
                <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
                  <CategoryIcon className="h-5 w-5 text-gray-600" />
                  {categoryInfo.name}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {categoryIntegrations.map((integration) => {
                    const IconComponent = integration.icon;
                    const installed = isInstalled(integration.key);
                    const available = isAvailable(integration.key);

                    return (
                      <Card
                        key={integration.key}
                        className={`transition-all duration-200 hover:shadow-md cursor-pointer ${
                          installed
                            ? 'ring-1 ring-green-400 bg-green-50'
                            : 'hover:scale-[1.01]'
                        }`}
                        onClick={() => handleIntegrationClick(integration.key)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="p-1.5 bg-gray-100 rounded">
                                <IconComponent className="h-4 w-4 text-gray-700" />
                              </div>
                              <div>
                                <h4 className="font-medium text-sm">
                                  {integration.name}
                                </h4>
                                {!available && (
                                  <p className="text-xs text-red-600 mt-1">
                                    {availabilityReason(integration.key) ||
                                      gt('Not available right now', {
                                        context:
                                          'integration unavailable fallback',
                                      })}
                                  </p>
                                )}
                              </div>
                            </div>
                            {installed && (
                              <Badge
                                variant="outline"
                                className="bg-green-100 text-green-700 border-green-300 text-xs"
                              >
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                <T context="integration installed badge">
                                  Installed
                                </T>
                              </Badge>
                            )}
                          </div>

                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {integration.description}
                          </p>

                          <Button
                            variant={installed ? 'outline' : 'default'}
                            size="sm"
                            className="w-full text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (installed) {
                                handleIntegrationClick(integration.key);
                              } else {
                                handleConnect(integration.key, e);
                              }
                            }}
                            disabled={
                              isConnecting(integration.key) || !available
                            }
                          >
                            {isConnecting(integration.key) ? (
                              <>
                                <CircleNotchIcon className="h-3 w-3 mr-1 animate-spin" />
                                <T context="integration connecting button">
                                  Connecting...
                                </T>
                              </>
                            ) : installed ? (
                              <T context="integration configure button">
                                Configure
                              </T>
                            ) : available ? (
                              <T context="integration install button">
                                Install
                              </T>
                            ) : (
                              <T context="integration unavailable button">
                                Unavailable
                              </T>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          },
        )}
      </div>
    </DashboardLayout>
  );
}

function FeaturedIntegrations({
  allIntegrations,
  isInstalled,
  isAvailable,
  availabilityReason,
  handleIntegrationClick,
  handleConnect,
  isConnecting,
  notAvailableFallback,
}: {
  allIntegrations: IntegrationConfig[];
  isInstalled: (key: IntegrationKey) => boolean;
  isAvailable: (key: IntegrationKey) => boolean;
  availabilityReason: (key: IntegrationKey) => string | undefined;
  handleIntegrationClick: (key: IntegrationKey) => void;
  handleConnect: (key: IntegrationKey, event: React.MouseEvent) => void;
  isConnecting: (key: IntegrationKey) => boolean;
  notAvailableFallback: string;
}) {
  const featuredIntegrations = allIntegrations.filter(
    (integration) => integration.isPopular,
  );

  if (featuredIntegrations.length === 0) return null;

  return (
    <div className="mb-3">
      <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
        <StarIcon className="h-5 w-5 text-yellow-500" />
        <T context="integrations featured section title">
          Featured Integrations
        </T>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {allIntegrations
          .filter((integration) => integration.isPopular)
          .map((integration) => {
            const IconComponent = integration.icon;
            const categoryInfo = INTEGRATION_CATEGORIES[integration.category];
            const installed = isInstalled(integration.key);
            const available = isAvailable(integration.key);

            return (
              <Card
                key={integration.key}
                className={`relative transition-all duration-200 hover:shadow-lg cursor-pointer ${
                  installed
                    ? 'ring-2 ring-green-500 bg-green-50'
                    : 'hover:scale-[1.02]'
                }`}
                onClick={() => handleIntegrationClick(integration.key)}
              >
                {installed && (
                  <div className="absolute top-3 right-3">
                    <Badge
                      variant="outline"
                      className="bg-green-100 text-green-700 border-green-300"
                    >
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      <T context="integration installed badge">Installed</T>
                    </Badge>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <IconComponent className="h-6 w-6 text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">
                        {integration.name}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {categoryInfo.name}
                        </Badge>
                        {integration.isPopular && (
                          <Badge variant="outline" className="text-xs">
                            <StarIcon className="h-3 w-3 mr-1" />
                            <T context="popular integration badge">Popular</T>
                          </Badge>
                        )}
                        {!available && (
                          <Badge
                            variant="outline"
                            className="text-xs border-red-300 text-red-700 bg-red-50"
                          >
                            <T context="integration unavailable badge">
                              Unavailable
                            </T>
                          </Badge>
                        )}
                      </div>
                      {!available && (
                        <p className="text-xs text-red-600 mt-1">
                          {availabilityReason(integration.key) ||
                            notAvailableFallback}
                        </p>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <CardDescription className="mb-2 line-clamp-2">
                    {integration.description}
                  </CardDescription>

                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        <T context="integration key features heading">
                          Key Features:
                        </T>
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {integration.features
                          .slice(0, 3)
                          .map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 bg-gray-400 rounded-full"></div>
                              {feature}
                            </li>
                          ))}
                        {integration.features.length > 3 && (
                          <li className="text-xs text-gray-500">
                            <T>
                              +<Var>{integration.features.length - 3}</Var> more
                              features
                            </T>
                          </li>
                        )}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <Button
                        variant={installed ? 'outline' : 'default'}
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (installed) {
                            handleIntegrationClick(integration.key);
                          } else {
                            handleConnect(integration.key, e);
                          }
                        }}
                        disabled={isConnecting(integration.key) || !available}
                      >
                        {isConnecting(integration.key) ? (
                          <>
                            <CircleNotchIcon className="h-3 w-3 animate-spin" />
                            <T context="integration connecting button">
                              Connecting...
                            </T>
                          </>
                        ) : (
                          <>
                            {installed ? (
                              <T context="integration configure button">
                                Configure
                              </T>
                            ) : available ? (
                              <T context="integration install button">
                                Install
                              </T>
                            ) : (
                              <T context="integration unavailable button">
                                Unavailable
                              </T>
                            )}
                            <ArrowRightIcon className="h-3 w-3" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>
    </div>
  );
}
